import React from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { clsx } from 'clsx';
import { Switch } from 'src/components/ui/switch';
import { Input as CNInput } from 'src/components/ui/input';

// Used by the project settings form and the integrations' advanced settings forms.
interface AutoCloseFieldProps<T extends FieldValues> {
  control: Control<T>;
  label: React.ReactNode;
  toggleName: FieldPath<T>;
  limitHourName: FieldPath<T>;
  limitMinuteName: FieldPath<T>;
  className?: string;
  disabled?: boolean;
}

export const AutoCloseField = <T extends FieldValues>({
  control,
  label,
  toggleName,
  limitHourName,
  limitMinuteName,
  className = '',
  disabled = false,
}: AutoCloseFieldProps<T>) => {
  return (
    <div className={clsx('content', className)}>
      <div>
        <div className="flex gap-2.5">
          <Controller
            control={control}
            name={toggleName}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Switch checked={value} onCheckedChange={onChange} />
            )}
          />
          <div className="flex flex-col gap-2.5">
            <label className="opacity-80 font-semibold text-sm text-ebony font-jamjuree">
              {label}
            </label>
            <div className="flex flex-row gap-2.5">
              <div className="flex gap-1.5 items-center">
                <Controller
                  control={control}
                  name={limitHourName}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <CNInput
                        disabled={disabled}
                        placeholder="00"
                        min={0}
                        type={'number'}
                        className={clsx(
                          'py-0.5 px-3.5 h-7 rounded-sm-5 w-fit w-24',
                          error?.message && 'border-error placeholder:text-error text-error',
                        )}
                        {...field}
                      />
                      <p
                        className={clsx(
                          'font-medium text-sm text-ebony',
                          error?.message ? 'text-error' : 'opacity-50',
                        )}
                      >
                        hours
                      </p>
                    </>
                  )}
                />
              </div>
              <div className="flex gap-1.5 items-center">
                <Controller
                  control={control}
                  name={limitMinuteName}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <CNInput
                        disabled={disabled}
                        placeholder="00"
                        min={0}
                        max={59}
                        type={'number'}
                        className={clsx(
                          'py-0.5 px-3.5 h-7 rounded-sm-5 w-fit w-24',
                          error?.message && 'border-error placeholder:text-error text-error',
                        )}
                        {...field}
                      />
                      <p
                        className={clsx(
                          'font-medium text-sm text-ebony',
                          error?.message ? 'text-error' : 'opacity-50',
                        )}
                      >
                        minutes
                      </p>
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
